import { useEffect, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import { Command as CommandPrimitive } from 'cmdk';
import { ChevronDown, Loader2, X } from 'lucide-react';
import { Command, CommandItem, CommandList } from '@/components/ui/command';
import { cn } from '@/lib/utils';

export interface MultiSelectComboboxOption {
  value: string;
  label: string;
}

export interface MultiSelectComboboxProps {
  /** Accessible name for the input (cmdk sets the input's own id and aria-labelledby). */
  label?: string;
  /** Options to list. The caller filters them (for example, by a server-side search); this component doesn't. */
  options: MultiSelectComboboxOption[];
  /** The current selection, shown as chips in selection order. */
  selectedOptions: MultiSelectComboboxOption[];
  /**
   * Custom chip content (for example, an inline editor); defaults to the option's label.
   * The chip hides its remove button while the content has an element marked `data-editing`.
   */
  renderChipLabel?: (option: MultiSelectComboboxOption) => ReactNode;
  onChange: (value: string[]) => void;
  searchValue: string;
  onSearchChange: (value: string) => void;
  onBlur?: () => void;
  placeholder?: string;
  emptyText?: string;
  noMatchText?: string;
  allSelectedText?: string;
  disabled?: boolean;
  disabledText?: string;
  isLoading?: boolean;
  isSearching?: boolean;
  isError?: boolean;
  errorText?: string;
  onRetry?: () => void;
  error?: string;
}

const MultiSelectCombobox = ({
  label,
  options,
  selectedOptions,
  renderChipLabel,
  onChange,
  searchValue,
  onSearchChange,
  onBlur,
  placeholder = 'Search',
  emptyText = 'No options available',
  noMatchText = 'No matching options',
  allSelectedText = 'All options selected',
  disabled = false,
  disabledText,
  isLoading = false,
  isSearching = false,
  isError = false,
  errorText = "Couldn't load options",
  onRetry,
  error,
}: MultiSelectComboboxProps) => {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const selectedValues = selectedOptions.map((option) => option.value);
  const visibleOptions = options.filter((option) => !selectedValues.includes(option.value));
  const hasSearch = searchValue.trim().length > 0;

  const close = () => {
    setOpen(false);
    onBlur?.();
  };

  // Rendered inline (not portaled), so close on outside clicks ourselves.
  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setOpen(false);
        onBlur?.();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open, onBlur]);

  const select = (value: string) => {
    onChange([...selectedValues, value]);
    onSearchChange('');
    inputRef.current?.focus();
  };

  const remove = (value: string) => {
    onChange(selectedValues.filter((selected) => selected !== value));
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      close();
    } else if (event.key === 'Backspace' && !searchValue && selectedValues.length) {
      remove(selectedValues[selectedValues.length - 1]);
    } else if (!open) {
      setOpen(true);
    }
  };

  const renderStatus = () => {
    if (isLoading) {
      return (
        <div className="flex items-center justify-center gap-2 py-4 text-sm text-[#676767]">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading…
        </div>
      );
    }
    if (isError) {
      return (
        <div className="flex items-center justify-center gap-2 py-4 text-sm text-error">
          {errorText}
          {onRetry && (
            <button
              type="button"
              className="font-medium text-focuscolor underline"
              onMouseDown={(event) => event.preventDefault()}
              onClick={onRetry}
            >
              Retry
            </button>
          )}
        </div>
      );
    }
    if (visibleOptions.length) return null;
    const text = hasSearch ? noMatchText : options.length ? allSelectedText : emptyText;
    return <div className="py-4 text-center text-sm text-[#676767]">{text}</div>;
  };

  return (
    <div className="relative flex flex-col gap-1" ref={containerRef}>
      <Command label={label} shouldFilter={false} className="overflow-visible bg-transparent">
        <div
          className={cn(
            'flex min-h-12 w-full items-center gap-2 rounded-md border border-bordercolor bg-white px-3 py-2 text-sm',
            open && 'border-focuscolor',
            error && 'border-error',
            disabled ? 'cursor-not-allowed opacity-60' : 'cursor-text'
          )}
          onClick={() => {
            if (disabled) return;
            inputRef.current?.focus();
            setOpen(true);
          }}
        >
          <div className="flex flex-1 flex-wrap items-center gap-2">
            {selectedOptions.map((option) => (
              <span
                key={option.value}
                className="group inline-flex items-start gap-1 rounded-full bg-[#EDF5FF] px-3 py-1 text-sm leading-5 text-primary"
              >
                {renderChipLabel ? renderChipLabel(option) : option.label}
                {!disabled && (
                  <button
                    type="button"
                    aria-label={`Remove ${option.label}`}
                    className="flex h-5 items-center rounded-full text-primary hover:text-error group-has-[[data-editing]]:hidden focus:outline-none focus-visible:ring-1 focus-visible:ring-focuscolor"
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={(event) => {
                      event.stopPropagation();
                      remove(option.value);
                    }}
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                )}
              </span>
            ))}
            <CommandPrimitive.Input
              ref={inputRef}
              value={searchValue}
              onValueChange={(value) => {
                onSearchChange(value);
                setOpen(true);
              }}
              onFocus={() => setOpen(true)}
              onBlur={(event) => {
                if (!containerRef.current?.contains(event.relatedTarget as Node | null)) close();
              }}
              onKeyDown={handleKeyDown}
              disabled={disabled}
              placeholder={disabled && disabledText ? disabledText : selectedOptions.length ? '' : placeholder}
              className="min-w-[120px] flex-1 bg-transparent outline-none placeholder:text-[#676767] disabled:cursor-not-allowed"
            />
          </div>
          {isSearching && !isLoading ? (
            <Loader2 aria-label="Searching" className="h-4 w-4 shrink-0 animate-spin text-[#969696]" />
          ) : (
            <ChevronDown className="h-4 w-4 shrink-0 text-[#969696]" />
          )}
        </div>
        {open && !disabled && (
          <div className="absolute top-full z-20 mt-1 w-full rounded-md border border-gray-300 bg-white shadow-lg">
            <CommandList className="max-h-60">
              {renderStatus()}
              {!isLoading &&
                !isError &&
                visibleOptions.map((option) => (
                  <CommandItem
                    key={option.value}
                    value={option.value}
                    onMouseDown={(event) => event.preventDefault()}
                    onSelect={() => select(option.value)}
                    className="cursor-pointer px-3 py-2 text-[#676767] data-[selected=true]:bg-gray-100 data-[selected=true]:text-primary"
                  >
                    {option.label}
                  </CommandItem>
                ))}
            </CommandList>
          </div>
        )}
      </Command>
      {error && <p className="text-xs text-error">{error}</p>}
    </div>
  );
};

export default MultiSelectCombobox;
